import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { UserPhoto } from '../models/generated/ziroModels';
import { chunk } from 'lodash';
import { HttpClient } from '@angular/common/http';
import { map, shareReplay } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ZpmUserPhotoContext {
  private _http = inject(HttpClient);

  private _stateSource = new ReplaySubject<number>(1);
  state$ = this._stateSource.asObservable();

  private _userPhotos: UserPhoto[] = [];
  private _requestedUserPhotos: Map<string, Observable<void>> = new Map();

  getUserPhotos(): UserPhoto[] {
    return this._userPhotos;
  }

  initUserPhotos(tenantId: string | number, upns: string[]): Observable<void> {
    return this._initUserPhotos(tenantId, upns, (id, batch) => this._macsSearchPhotos(id, batch));
  }

  initAutoAttendantUserPhotos(tenantId: string | number, upns: string[]): Observable<void> {
    return this._initUserPhotos(tenantId, upns, (id, batch) => this._autoAttendantsSearchPhotos(id, batch));
  }

  private _initUserPhotos(
    tenantId: string | number,
    upns: string[],
    searchFn: (tenantId: string | number, upns: string[]) => Observable<UserPhoto[]>,
  ): Observable<void> {
    const validUpns = upns.filter((upn) => upn);
    const newUpns = validUpns.filter((upn) => !this._requestedUserPhotos.has(upn));
    const batchedUpns = chunk(newUpns, 1000);
    const upnRequests = validUpns
      .filter((upn) => this._requestedUserPhotos.has(upn))
      .map((upn) => this._requestedUserPhotos.get(upn));
    const existingRequests = Array.from(new Set(upnRequests));

    const newRequests = batchedUpns.map((batch: string[]) => {
      const observable = searchFn(tenantId, batch).pipe(
        map((data: UserPhoto[]) => {
          this._userPhotos = this._userPhotos.concat(data);
        }),
        shareReplay(),
      );
      batch.forEach((upn) => this._requestedUserPhotos.set(upn, observable));
      return observable;
    });

    if (!newRequests.length && !existingRequests.length) {
      this._stateSource.next(Date.now());
      return of(null);
    }

    return forkJoin([...newRequests, ...existingRequests]).pipe(
      map(() => {
        this._stateSource.next(Date.now());
      }),
    );
  }

  private _macsSearchPhotos(tenantId: string | number, upns: string[], photoSize = '48x48'): Observable<UserPhoto[]> {
    return this._http.post<UserPhoto[]>(`/services/microsoft/macs/tenants/${tenantId}/user-photos/search`, {
      userPrincipalNames: upns,
      photoSize: photoSize,
    });
  }

  private _autoAttendantsSearchPhotos(
    tenantId: string | number,
    upns: string[],
    photoSize = '48x48',
  ): Observable<UserPhoto[]> {
    return this._http.post<UserPhoto[]>(
      `/services/microsoft/auto-attendant-management/tenants/${tenantId}/user-photos/search`,
      {
        userPrincipalNames: upns,
        photoSize: photoSize,
      },
    );
  }
}
